/**
 * A tela de UMA implantação no console — o que faltava para o consultor trabalhar sem `curl`.
 *
 * **O beco que ela fecha.** Até a F7 o console tinha a carteira (uma linha por cliente) e duas
 * telas soltas de detalhe: a revisão do kickoff e o cofre. Não existia lugar nenhum que
 * respondesse "o que está acontecendo com este cliente" — a esteira saía por
 * `/implantacoes/{id}/esteira`, a linha do tempo por `/eventos` e a liberação do ambiente só por
 * `POST`. Quem revisava chegava pela carteira, via as respostas e voltava sem saber se o ambiente
 * tinha sido liberado, o que havia de pendência ou quem tinha respondido o quê.
 *
 * Cinco decisões desta tela:
 *
 * · **Uma página, uma seção por vez (SUS-15400).** Reunir continua sendo a entrega: tudo está
 *   nesta tela. Empilhado, porém, achar um dado exigia rolar a página inteira. O cabeçalho
 *   responde as duas perguntas que trazem alguém aqui (onde está o cliente e se o relógio já
 *   começou) e a esteira fica na horizontal. Logo abaixo, sete cards com o resumo de cada seção
 *   abrem uma de cada vez. A seção aberta vai para o hash da URL, que é o link para mandar a um
 *   colega.
 *
 * · **A esteira é a mesma derivação que o cliente lê.** `/{id}/esteira` e `/minha` chamam a mesma
 *   função no servidor, e esta tela reaproveita os componentes do painel do cliente. É o que
 *   impede o console de dizer "aguardando infra" enquanto o cliente lê "concluído".
 *
 * · **Reunir o cofre não é revelá-lo.** Entram rótulo, procedência e histórico — nunca valor. O
 *   cofre inteiro se justifica pelo registro por revelação; uma tela de resumo que imprimisse a
 *   senha "porque já estou na implantação" apagaria esse registro sem mudar uma linha do
 *   servidor, e o cofre voltaria a ser o WhatsApp com mais passos.
 *
 * · **Cada bloco confere a permissão antes de pedir.** `contrato:ver`, `infra:ver`,
 *   `kickoff:revisar` e `pacote:gerar` estão em papéis diferentes da matriz da spec §4 — a
 *   infraestrutura libera o ambiente e não lê o contrato. A tela mostra o que a sessão alcança e
 *   explica o resto; a negativa de verdade continua sendo do servidor.
 */

import { ContatosDeAcesso } from "../componentes/console/ContatosDeAcesso";
import { ExcluirImplantacao } from "../componentes/console/ExcluirImplantacao";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import {
  Aviso,
  Botao,
  Cabecalho,
  Cartao,
  Celula,
  CelulaCabecalho,
  Chip,
  Corpo,
  Linha,
  Tabela,
} from "../componentes/ui";
import { EsteiraDaImplantacao, ResumoDaImplantacao } from "../componentes/esteira";
import {
  Contradicoes,
  LiberacaoDoAmbiente,
  LinhaDoTempo,
  MaisAcoes,
  NavegacaoDaImplantacao,
  RepasseDosModulos,
  type CardDaSecao,
} from "../componentes/console";
import {
  SECOES_DA_IMPLANTACAO,
  plural,
  resumoDoKickoff,
  resumoDoRepasse,
  secaoDoHash,
  type SecaoDaImplantacao,
} from "../nucleo/secoesDaImplantacao";
import { useContatos } from "../nucleo/contatosDeAcesso";
import { PERMISSAO_DE_VER_O_REPASSE, useRepasseDaImplantacao } from "../nucleo/repasse";
import { ErroDaApi } from "../nucleo/api";
import { useTitulo } from "../nucleo/titulo";
import { podeNaSessao, useSessao } from "../nucleo/sessao";
import {
  PERMISSAO_DE_EDITAR_IMPLANTACAO,
  PERMISSAO_DE_GERAR_PACOTE,
  PERMISSAO_DE_LIBERAR_AMBIENTE,
  PERMISSAO_DE_REVISAR_KICKOFF,
  PERMISSAO_DE_VER_COFRE,
  PERMISSAO_DE_VER_CONTRATO,
  PERMISSAO_DE_VER_INFRA,
  caminhoDoPacote,
  rotuloDaSituacao,
  rotuloDoAmbiente,
  rotuloDoEstado,
  mensagemDoConsole,
  useAnexarContrato,
  useDefinirConsultor,
  useDetalheDaImplantacao,
  useEsteiraDaImplantacao,
  useLinhaDoTempo,
} from "../nucleo/console";
import {
  rotuloDaVez,
  textoDaEsteira,
  formatarCnpj,
  formatarData,
  caminhoDoContrato,
  erroDoContrato,
  useCatalogo,
  type Esteira,
} from "../nucleo/implantacoes";
import {
  CHIP_DO_ESTADO as CHIP_DA_TRILHA,
  ROTULO_DO_ESTADO as ROTULO_DA_TRILHA,
  caminhoDaRevisao,
  nomeDaTrilhaNoConsole,
  origemDaTrilhaNoConsole,
  situacaoDaTrilha,
  useRevisaoDoKickoff,
} from "../nucleo/kickoff";
import {
  ROTULO_DA_CUSTODIA_DO_SEGREDO,
  caminhoDoCofre,
  formatarInstante,
  useCofreDaImplantacao,
} from "../nucleo/cofre";
import { estadoDoJira, rotuloDaLacuna, usePacotes } from "../nucleo/pacote";
import { PERMISSAO_DE_VER_O_JIRA, useJiraDaImplantacao } from "../nucleo/jira";
import { PERMISSAO_DE_VER_ATA, caminhoDasReunioes, useReunioes } from "../nucleo/reuniao";

export function ImplantacaoNoConsole() {
  const { id } = useParams();
  const implantacaoId = id ?? "";
  const esteira = useEsteiraDaImplantacao(implantacaoId);

  useTitulo("Implantação");

  if (esteira.isError) {
    const mensagem =
      esteira.error instanceof ErroDaApi
        ? esteira.error.message
        : "Não foi possível carregar esta implantação agora. Tente de novo em instantes.";
    return (
      <>
        <h1 className="titulo-pagina">Implantação</h1>
        <Aviso tom="erro" data-testid="falha-da-implantacao">
          {mensagem}
        </Aviso>
        <p className="nota-rodape mt-4">
          <Link to="/admin/implantacoes">Voltar à carteira</Link>
        </p>
      </>
    );
  }

  if (esteira.isPending) {
    return (
      <p className="lead" role="status">
        Carregando a implantação…
      </p>
    );
  }

  return <Implantacao esteira={esteira.data} implantacaoId={implantacaoId} />;
}

function Implantacao({
  esteira,
  implantacaoId,
}: {
  esteira: Esteira;
  implantacaoId: string;
}) {
  const [exclusao, setExclusao] = useState("");
  const sessao = useSessao();
  const podeVerContrato = podeNaSessao(sessao.data, PERMISSAO_DE_VER_CONTRATO);
  const detalhe = useDetalheDaImplantacao(implantacaoId, podeVerContrato);

  /* O nome vem do cadastro quando a sessão o alcança, e da esteira quando não. Os dois trazem a
     mesma organização; o cadastro é preferido porque é ele que carrega a razão social e o estado
     do contrato, e uma tela que misturasse as duas fontes no mesmo cabeçalho poderia mostrar um
     nome antigo ao lado de um dado novo. */
  const organizacao = detalhe.data?.organizacao ?? esteira.organizacao;
  const nome = organizacao.nome_fantasia ?? organizacao.razao_social;

  const location = useLocation();
  const navigate = useNavigate();
  const podeVerRepasse = podeNaSessao(sessao.data, PERMISSAO_DE_VER_O_REPASSE);
  /* O repasse é a única seção que some sem permissão (as outras explicam a negativa): o link
     direto para ela, aberto por quem não a alcança, cai em Contatos de acesso. */
  const disponiveis = SECOES_DA_IMPLANTACAO.filter(
    (secao) => secao.id !== "repasse" || podeVerRepasse,
  ).map((secao) => secao.id);
  const secao = secaoDoHash(location.hash, disponiveis);
  const [excluindo, setExcluindo] = useState(false);
  const maisAcoes = useRef<HTMLButtonElement>(null);
  const painel = useRef<HTMLDivElement>(null);
  const focarPainel = useRef(false);
  const cards = useCardsDasSecoes(implantacaoId, esteira, disponiveis);

  /* Depois do clique, o foco vai ao painel: quem navega por teclado ou leitor de tela chega à
     seção que acabou de abrir, em vez de ficar parado no card. */
  useEffect(() => {
    if (!focarPainel.current) return;
    focarPainel.current = false;
    painel.current?.focus({ preventScroll: true });
    painel.current?.scrollIntoView?.({ block: "nearest" });
  }, [secao]);

  function abrir(destino: SecaoDaImplantacao) {
    /* O card da seção já aberta só leva o foco ao painel. Navegar de novo empilharia uma entrada
       repetida (o Voltar pareceria não fazer nada), e a flag armada sem o efeito rodar puxaria o
       foco num Voltar qualquer, mais tarde, sem ninguém ter pedido. */
    if (destino === secao) {
      painel.current?.focus({ preventScroll: true });
      return;
    }
    focarPainel.current = true;
    navigate({ pathname: location.pathname, search: location.search, hash: destino });
  }

  if (exclusao)
    return (
      <>
        <h1 className="titulo-pagina">Implantação excluída</h1>
        <div role="status">
          <Aviso>{exclusao}</Aviso>
        </div>
        <Link to="/admin/implantacoes">Voltar à carteira</Link>
      </>
    );

  const liberado = esteira.prazo.ambiente_liberado_em !== null;

  return (
    <>
      <div className="cabecalho-da-trilha">
        <div>
          <h1 className="titulo-pagina">{nome}</h1>
          <p className="lead mb-2">
            <span className="numero-tabular">{formatarCnpj(organizacao.cnpj)}</span>
            {organizacao.cidade ? (
              <>
                {" · "}
                {organizacao.cidade}/{organizacao.uf}
              </>
            ) : null}
            {esteira.consultor ? <> · Consultor: {esteira.consultor.nome}</> : null}
          </p>
        </div>
        <div className="cabecalho-da-implantacao__acoes">
          <Chip
            estado={esteira.vez === "cliente" ? "aguardando" : "neutro"}
            data-testid="vez-da-implantacao"
          >
            {rotuloDaVez(esteira.vez, "interna")}
          </Chip>
          <MaisAcoes
            refDoBotao={maisAcoes}
            acoes={
              podeNaSessao(sessao.data, "implantacao:excluir")
                ? [
                    {
                      rotulo: "Excluir implantação",
                      testid: "acao-excluir-implantacao",
                      perigosa: true,
                      aoEscolher: () => setExcluindo(true),
                    },
                  ]
                : []
            }
          />
        </div>
      </div>

      {excluindo ? (
        <ExcluirImplantacao
          id={implantacaoId}
          nome={nome}
          podeExcluir={podeNaSessao(sessao.data, "implantacao:excluir")}
          aoExcluir={setExclusao}
          aoCancelar={() => {
            setExcluindo(false);
            maisAcoes.current?.focus();
          }}
        />
      ) : null}

      {/* Prazo, liberação e as cinco etapas ficam fora dos cards porque são as duas perguntas que
          trazem alguém a esta tela: onde está o cliente, e se o relógio contratual já começou. */}
      <section
        className="mb-4"
        aria-labelledby="titulo-da-esteira"
        data-testid="esteira-da-implantacao"
      >
        <h2 className="sr-only" id="titulo-da-esteira">
          Esteira e prazo
        </h2>
        <div className="cabecalho-da-implantacao__resumo">
          <ResumoDaImplantacao esteira={esteira} voz="interna" />
          {liberado ? (
            <SeloDaLiberacao prazo={esteira.prazo} />
          ) : (
            <LiberacaoDoAmbiente
              implantacaoId={implantacaoId}
              prazo={esteira.prazo}
              cliente={nome}
              podeLiberar={podeNaSessao(sessao.data, PERMISSAO_DE_LIBERAR_AMBIENTE)}
              podeVerInfra={podeNaSessao(sessao.data, PERMISSAO_DE_VER_INFRA)}
              compacta
            />
          )}
        </div>
        <p className="lead proximo-passo-da-implantacao my-3" data-testid="proximo-passo-interno">
          <b>Próximo passo:</b> {textoDaEsteira(esteira.proximo_passo, "interna")}
        </p>
        <JiraPendente implantacaoId={implantacaoId} />
        <EsteiraDaImplantacao esteira={esteira} voz="interna" aoAbrirSecao={abrir} />
      </section>

      {esteira.pendencias.length > 0 ? (
        <section className="mb-4" aria-labelledby="titulo-das-pendencias">
          <h2 className="titulo-secao" id="titulo-das-pendencias">
            Pendências abertas
          </h2>
          <div className="grid gap-3 md:grid-cols-2" data-testid="pendencias-da-implantacao">
            {esteira.pendencias.map((pendencia) => (
              <Cartao
                key={pendencia.referencia}
                titulo={pendencia.titulo}
                acessorio={
                  <Chip estado={pendencia.de_quem === "cliente" ? "atencao" : "neutro"}>
                    {pendencia.de_quem === "cliente" ? "com o cliente" : "com a GEOvendas"}
                  </Chip>
                }
              >
                {pendencia.descricao ? (
                  <p className="cartao__texto">{pendencia.descricao}</p>
                ) : null}
                <p className="nota-rodape mt-2 numero-tabular">
                  {pendencia.referencia} · aberta em {formatarInstante(pendencia.aberta_em) ?? "—"}
                </p>
              </Cartao>
            ))}
          </div>
        </section>
      ) : null}

      <NavegacaoDaImplantacao cards={cards} ativa={secao} aoAbrir={abrir} />

      {/* Sem role nem nome próprios: cada seção já é uma região com o seu título, e um invólucro
          com o mesmo nome em volta dela seria a mesma região anunciada duas vezes. */}
      <div
        ref={painel}
        className="painel-da-implantacao"
        tabIndex={-1}
        data-testid="painel-da-implantacao"
        data-secao={secao}
      >
        {secao === "contatos" ? (
          <ContatosDeAcesso
            id={implantacaoId}
            cnpj={organizacao.cnpj}
            podeVer={podeNaSessao(sessao.data, "console:ver")}
            podeEditar={podeNaSessao(sessao.data, PERMISSAO_DE_EDITAR_IMPLANTACAO)}
          />
        ) : secao === "kickoff" ? (
          <Kickoff implantacaoId={implantacaoId} />
        ) : secao === "reunioes" ? (
          <Reunioes implantacaoId={implantacaoId} />
        ) : secao === "repasse" ? (
          <RepasseDosModulos implantacaoId={implantacaoId} />
        ) : secao === "credenciais" ? (
          <SegredosDoCofre implantacaoId={implantacaoId} />
        ) : secao === "contrato" ? (
          <section className="mb-6" aria-labelledby="titulo-do-contrato">
            <h2 className="titulo-secao" id="titulo-do-contrato">
              Contrato e escopo
            </h2>
            <Contrato esteira={esteira} podeVerContrato={podeVerContrato} detalhe={detalhe.data} />
          </section>
        ) : (
          <section className="mb-6" aria-labelledby="titulo-do-historico">
            <h2 className="titulo-secao" id="titulo-do-historico">
              Linha do tempo
            </h2>
            <Historico implantacaoId={implantacaoId} />
          </section>
        )}
      </div>

      <p className="nota-rodape mt-5">
        <Link to="/admin/implantacoes">Voltar à carteira</Link>
      </p>
    </>
  );
}

/* O selo que substitui o aviso grande "Ambiente liberado" depois da liberação: a data em que o
   relógio começou (ou o que falta para ele começar) cabe numa linha do cabeçalho. */
function SeloDaLiberacao({ prazo }: { prazo: Esteira["prazo"] }) {
  const inicio = formatarData(prazo.inicio_do_prazo_em ?? null);
  const liberadoEm = formatarData(prazo.ambiente_liberado_em);
  return (
    <div
      className="selo-da-liberacao"
      data-testid="liberacao-do-ambiente"
      data-liberado="sim"
      data-relogio={prazo.relogio_iniciado ? "sim" : "nao"}
    >
      <Chip estado={prazo.relogio_iniciado ? "ok" : "aguardando"}>
        {prazo.relogio_iniciado
          ? "Ambiente liberado · prazo correndo"
          : "Ambiente liberado · aguardando geração da tarefa de kickoff"}
      </Chip>
      <p className="nota-rodape mt-1 numero-tabular">
        {prazo.relogio_iniciado && inicio
          ? `Contando desde ${inicio}`
          : `Ambiente liberado em ${liberadoEm ?? "—"} · entrega prevista a definir`}
      </p>
    </div>
  );
}

/* O resumo de cada card sai das mesmas consultas que as seções fazem (mesma chave, então o
   react-query não repete a requisição): a tela pede o que já pedia quando era empilhada. */
function useCardsDasSecoes(
  implantacaoId: string,
  esteira: Esteira,
  disponiveis: readonly SecaoDaImplantacao[],
): CardDaSecao[] {
  const sessao = useSessao();
  const podeVerConsole = podeNaSessao(sessao.data, "console:ver");
  const podeRevisar = podeNaSessao(sessao.data, PERMISSAO_DE_REVISAR_KICKOFF);
  const podeGerar = podeNaSessao(sessao.data, PERMISSAO_DE_GERAR_PACOTE);
  const podeVerCofre = podeNaSessao(sessao.data, PERMISSAO_DE_VER_COFRE);
  const contatos = useContatos(implantacaoId, podeVerConsole);
  const revisao = useRevisaoDoKickoff(podeRevisar ? implantacaoId : "");
  const pacotes = usePacotes(podeGerar ? implantacaoId : "");
  const podeVerReunioes = podeNaSessao(sessao.data, PERMISSAO_DE_VER_ATA);
  const reunioes = useReunioes(podeVerReunioes ? implantacaoId : "");
  const repasse = useRepasseDaImplantacao(implantacaoId, disponiveis.includes("repasse"));
  const cofre = useCofreDaImplantacao(podeVerCofre ? implantacaoId : "");
  const eventos = useLinhaDoTempo(implantacaoId);

  const resumos: Record<SecaoDaImplantacao, Omit<CardDaSecao, "id" | "titulo">> = {
    contatos: {
      resumo: !podeVerConsole
        ? "Só a equipe interna"
        : contatos.data
          ? `${plural(contatos.data.contatos.length, "contato", "contatos")} · ${plural(
              contatos.data.contatos.filter((contato) => contato.aceito_em).length,
              "acesso aceito",
              "acessos aceitos",
            )}`
          : null,
    },
    kickoff: {
      resumo: !podeRevisar
        ? "Trilhas e respostas"
        : revisao.data
          ? resumoDoKickoff(revisao.data.trilhas.map((trilha) => trilha.progresso.percentual))
          : null,
      alerta: pacotes.data?.pacotes[0]?.desatualizado ? "Resumo desatualizado" : null,
    },
    reunioes: {
      resumo: !podeVerReunioes
        ? "Agenda, gravação e ata"
        : reunioes.data
          ? `${plural(
              reunioes.data.reunioes.filter((reuniao) => reuniao.estado === "realizada").length,
              "realizada",
              "realizadas",
            )} · ${plural(
              reunioes.data.reunioes.filter((reuniao) => reuniao.estado === "agendada").length,
              "agendada",
              "agendadas",
            )}`
          : null,
    },
    repasse: {
      resumo: repasse.data ? resumoDoRepasse(repasse.data.modulos) : null,
    },
    credenciais: {
      resumo: !podeVerCofre
        ? "Com a infraestrutura"
        : cofre.data
          ? `${cofre.data.segredos.length} no cofre · ${plural(
              cofre.data.segredos.reduce((total, segredo) => total + segredo.revelacoes, 0),
              "revelação",
              "revelações",
            )}`
          : null,
    },
    contrato: {
      resumo: `${plural(esteira.modulos.length, "módulo", "módulos")} · ${esteira.recursos_plus.length} Recursos Plus`,
    },
    linha: {
      resumo: eventos.data
        ? `${plural(eventos.data.eventos.length, "evento", "eventos")}${
            eventos.data.eventos[0]
              ? ` · último ${formatarData(eventos.data.eventos[0].ocorrido_em) ?? "—"}`
              : ""
          }`
        : null,
    },
  };

  return SECOES_DA_IMPLANTACAO.filter((secao) => disponiveis.includes(secao.id)).map((secao) => ({
    id: secao.id,
    titulo: secao.titulo,
    ...resumos[secao.id],
  }));
}

function Dado({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div>
      <dt className="campo__rotulo">{rotulo}</dt>
      <dd className="m-0 text-sm">{children}</dd>
    </div>
  );
}

/* ----------------------------------------------------------------------------- kickoff */

function Kickoff({ implantacaoId }: { implantacaoId: string }) {
  const sessao = useSessao();
  const podeRevisar = podeNaSessao(sessao.data, PERMISSAO_DE_REVISAR_KICKOFF);
  const revisao = useRevisaoDoKickoff(podeRevisar ? implantacaoId : "");

  return (
    <section className="mb-6" aria-labelledby="titulo-do-kickoff">
      <h2 className="titulo-secao" id="titulo-do-kickoff">
        Kickoff
      </h2>
      <CorpoDoKickoff implantacaoId={implantacaoId} podeRevisar={podeRevisar} revisao={revisao} />
    </section>
  );
}

function CorpoDoKickoff({
  implantacaoId,
  podeRevisar,
  revisao,
}: {
  implantacaoId: string;
  podeRevisar: boolean;
  revisao: ReturnType<typeof useRevisaoDoKickoff>;
}) {
  if (!podeRevisar) {
    return (
      <Aviso tom="marca" data-testid="sem-kickoff-revisar">
        <b>As respostas do kickoff são lidas pela consultoria.</b> É ela que confirma com o
        cliente o que está divergente antes de a configuração começar.
      </Aviso>
    );
  }

  if (revisao.isPending) {
    return (
      <p className="lead" role="status">
        Carregando as trilhas…
      </p>
    );
  }

  if (revisao.isError) {
    return (
      <Aviso tom="erro" data-testid="falha-do-kickoff">
        {revisao.error instanceof ErroDaApi
          ? revisao.error.message
          : "Não foi possível carregar as respostas do kickoff agora."}
      </Aviso>
    );
  }

  return (
    <>
      {/* A divergência aparece já aqui, e não só na tela de revisão: é o achado que muda a
          conversa da reunião de kickoff, e quem abre o console para ver o andamento não passa
          necessariamente pela revisão. O identificador é próprio para que um teste consiga dizer
          QUAL das duas telas parou de mostrá-la. */}
      <Contradicoes contradicoes={revisao.data.contradicoes} />

      {revisao.data.modulos_sem_trilha.length > 0 ? (
        <Aviso tom="atencao" className="mb-4" data-testid="modulos-sem-trilha">
          <b>
            O kickoff não perguntou nada sobre{" "}
            {revisao.data.modulos_sem_trilha.map((modulo) => modulo.nome).join(", ")}.
          </b>{" "}
          O módulo foi contratado e ainda não tem perguntas no kickoff — o cliente respondeu
          tudo o que lhe foi oferecido.
        </Aviso>
      ) : null}

      {revisao.data.trilhas.length === 0 ? (
        <Aviso tom="marca" data-testid="sem-trilhas-na-implantacao">
          Nenhuma trilha de kickoff foi liberada para esta implantação até agora.
        </Aviso>
      ) : (
        <div data-testid="trilhas-da-implantacao">
          <Tabela legenda="Trilhas do kickoff, com estado, quem respondeu, progresso e status.">
          <Cabecalho>
            <Linha>
              <CelulaCabecalho>Trilha</CelulaCabecalho>
              <CelulaCabecalho>Estado</CelulaCabecalho>
              <CelulaCabecalho>Quem respondeu</CelulaCabecalho>
              <CelulaCabecalho>Enviada em</CelulaCabecalho>
              <CelulaCabecalho>Progresso</CelulaCabecalho>
              <CelulaCabecalho>Status</CelulaCabecalho>
            </Linha>
          </Cabecalho>
          <Corpo>
            {revisao.data.trilhas.map((trilha) => (
              <Linha key={trilha.chave}>
                <Celula className="celula-nome">
                  {/* A infraestrutura não é trilha do kickoff: é o pré-requisito da liberação, e
                      se chama pelo que é (spec de 28/09, 6.3). */}
                  <Link to={caminhoDaRevisao(implantacaoId, { trilha: trilha.chave })}>
                    {nomeDaTrilhaNoConsole(trilha)}
                  </Link>
                  <div className="celula-secundaria">{origemDaTrilhaNoConsole(trilha.origem)}</div>
                </Celula>
                <Celula>
                  <Chip
                    estado={CHIP_DA_TRILHA[trilha.sessao.estado]}
                    data-testid={`trilha-${trilha.chave}`}
                  >
                    {ROTULO_DA_TRILHA[trilha.sessao.estado]}
                  </Chip>
                </Celula>
                <Celula className="celula-secundaria">
                  {trilha.sessao.responsavel?.nome ?? "ninguém abriu ainda"}
                </Celula>
                <Celula className="numero-tabular celula-secundaria">
                  {formatarInstante(trilha.sessao.enviada_em) ?? "—"}
                </Celula>
                {/* O percentual mede só as obrigatórias e só fecha em 100% com a trilha enviada;
                    o contador embaixo continua contando todas — é ele que mostra o que ficou em
                    branco (spec de 28/09, 6.2). */}
                <Celula className="numero-tabular" data-testid={`progresso-de-${trilha.chave}`}>
                  {trilha.progresso.percentual}%
                  <div className="celula-secundaria">
                    {trilha.progresso.respondidas} de {trilha.progresso.perguntas_visiveis}{" "}
                    respondidas
                  </div>
                </Celula>
                <Celula>
                  <Chip
                    estado={situacaoDaTrilha(trilha.progresso).chip}
                    data-testid={`status-de-${trilha.chave}`}
                  >
                    {situacaoDaTrilha(trilha.progresso).rotulo}
                  </Chip>
                </Celula>
              </Linha>
            ))}
            </Corpo>
          </Tabela>
        </div>
      )}

      <div className="acoes-do-console mt-4">
        {/* A leitura pergunta a pergunta e os dois atos do consultor moram na revisão, que
            reaproveita a renderização do formulário do cliente. Repeti-los aqui criaria uma
            segunda renderização das respostas — que é o defeito que a revisão evita. */}
        <Link
          to={caminhoDaRevisao(implantacaoId)}
          className="botao"
          data-testid="ver-kickoff"
        >
          Revisar as respostas
        </Link>
        <Link
          to={caminhoDoPacote(implantacaoId)}
          className="botao botao--fantasma"
          data-testid="abrir-pacote"
        >
          Resumo do Kick Off
        </Link>
      </div>

      <PacoteResumido implantacaoId={implantacaoId} />
    </>
  );
}

/**
 * O que o portal ainda não conseguiu fazer no Jira — e por quê. Some quando não há pendência.
 *
 * É espera, não alarme: a lista nomeia o ato (mover o épico, criar a issue de Kick Off, anexar os
 * arquivos) e cita o motivo do servidor. O aceite do kickoff já está gravado, e o reprocessamento
 * cumpre cada item sozinho quando o obstáculo some.
 */
function JiraPendente({ implantacaoId }: { implantacaoId: string }) {
  const sessao = useSessao();
  const pode = podeNaSessao(sessao.data, PERMISSAO_DE_VER_O_JIRA);
  const jira = useJiraDaImplantacao(implantacaoId, pode);

  if (!pode || jira.isPending || jira.isError || jira.data.pendencias.length === 0) return null;

  return (
    <Aviso tom="atencao" className="mb-4" data-testid="pendencias-do-jira">
      <b>
        {jira.data.pendencias.length === 1
          ? "Uma ação no Jira está pendente."
          : `${jira.data.pendencias.length} ações no Jira estão pendentes.`}
      </b>{" "}
      O portal repete sozinho quando o motivo deixar de valer.
      <ul className="lista-de-marcas mt-2">
        {jira.data.pendencias.map((pendencia) => (
          <li className="lista-de-marcas__item" key={pendencia.assunto}>
            {pendencia.assunto}
            <span className="dado-secundario">{pendencia.motivo}</span>
          </li>
        ))}
      </ul>
    </Aviso>
  );
}

/**
 * O estado do pacote ao lado das trilhas — porque é aqui que a pergunta aparece.
 *
 * A que interessa não é "existe pacote?", e sim **"o pacote ainda corresponde ao que o cliente
 * respondeu?"**. Uma resposta alterada depois da geração marca o retrato como desatualizado no
 * banco, e um aviso que só existisse atrás de mais um clique chegaria tarde: quando alguém abre a
 * tela do pacote, já é para copiar.
 */
function PacoteResumido({ implantacaoId }: { implantacaoId: string }) {
  const sessao = useSessao();
  const podeGerar = podeNaSessao(sessao.data, PERMISSAO_DE_GERAR_PACOTE);
  const pacotes = usePacotes(podeGerar ? implantacaoId : "");

  if (!podeGerar || pacotes.isPending || pacotes.isError) return null;

  const ultimo = pacotes.data.pacotes[0];
  if (!ultimo) {
    /* Silêncio e "ainda não geramos" são coisas diferentes, e só a segunda informa. É a mesma
       regra do bloco de contradição: quem lê precisa saber se o portal conferiu e não achou nada
       ou se a conferência não existe. */
    return (
      <p className="nota-rodape mt-3" data-testid="sem-pacote">
        O resumo para a tarefa de Kick Off ainda não foi gerado.
      </p>
    );
  }

  const jira = estadoDoJira(ultimo);

  return (
    <Cartao
      className="mt-4"
      titulo={`Resumo do Kick Off, versão ${ultimo.versao}`}
      data-testid="resumo-do-pacote"
      data-desatualizado={ultimo.desatualizado ? "sim" : "nao"}
      acessorio={
        <Chip estado={ultimo.desatualizado ? "atencao" : "ok"}>
          {ultimo.desatualizado ? "desatualizado" : "em dia"}
        </Chip>
      }
    >
      <p className="cartao__texto">
        Gerado em {formatarInstante(ultimo.gerado_em) ?? "—"}
        {ultimo.gerado_por ? ` por ${ultimo.gerado_por.nome}` : ""}.{" "}
        {jira.estado === "enviado" ? (
          <>
            Tarefa <code>{jira.chave}</code> criada no Jira.
          </>
        ) : jira.estado === "pendente" ? (
          /* O motivo do servidor fica na tela do resumo, em "Detalhes técnicos" (spec 4.4). */
          <>A tarefa de Kick Off ainda não foi criada no Jira — o motivo está na tela do resumo.</>
        ) : (
          <>Nenhuma tentativa de envio ao Jira registrada.</>
        )}
      </p>
      {ultimo.desatualizado ? (
        <Aviso tom="atencao" className="mt-3" data-testid="pacote-desatualizado-no-console">
          <b>Alguma resposta mudou depois deste retrato.</b> Gere uma versão nova antes de levar
          qualquer coisa para o Jira — este texto descreve o kickoff de{" "}
          {formatarInstante(ultimo.desatualizado_em) ?? "antes da alteração"}, não o de agora.
        </Aviso>
      ) : null}
      {ultimo.lacunas.length > 0 ? (
        <p className="nota-rodape mt-2" data-testid="lacunas-no-resumo">
          {ultimo.lacunas.length} lacuna{ultimo.lacunas.length === 1 ? "" : "s"} registrada
          {ultimo.lacunas.length === 1 ? "" : "s"} no retrato — a primeira é{" "}
          {rotuloDaLacuna(ultimo.lacunas[0]?.motivo ?? "").toLowerCase()}; as outras aparecem na
          tela do resumo.
        </p>
      ) : null}
    </Cartao>
  );
}

/* ---------------------------------------------------------------------------- reuniões */

/**
 * O caminho para a agenda e as atas deste cliente.
 *
 * **Um ponteiro, e não um resumo.** Esta tela já reúne cinco fontes e faz cinco consultas; a
 * agenda tem endereço próprio porque uma reunião é algo a que se volta, e uma ata é reaberta
 * semanas depois da conversa. Trazer a lista para cá custaria mais duas consultas em toda visita
 * para responder uma pergunta que tem tela dedicada — e reconstruiria dentro de uma página a
 * dispersão que ela veio desfazer.
 */
function Reunioes({ implantacaoId }: { implantacaoId: string }) {
  return (
    <section className="mb-6" aria-labelledby="titulo-das-reunioes">
      <h2 className="titulo-secao" id="titulo-das-reunioes">
        Reuniões de Implantação
      </h2>
      <Cartao titulo="Agenda, gravação e ata" data-testid="chamada-das-reunioes">
        <p className="cartao__texto">
          O horário de cada conversa, o material dela e a ata — com o que vai ao cliente e o que
          fica só aqui dentro. As pendências combinadas em reunião também saem de lá: elas
          atravessam reuniões, e é por isso que ficam numa página só.
        </p>
        <p className="nota-rodape mt-2">
          Marque a reunião no seu calendário, como de costume, e registre aqui o horário e o link.
        </p>
        <div className="acoes-do-console mt-3">
          <Link
            to={caminhoDasReunioes(implantacaoId)}
            className="botao botao--fantasma"
            data-testid="abrir-agenda"
          >
            Abrir a agenda e as atas
          </Link>
        </div>
      </Cartao>
    </section>
  );
}

/* ------------------------------------------------------------------------------- cofre */

function SegredosDoCofre({ implantacaoId }: { implantacaoId: string }) {
  return (
    <section className="mb-6" aria-labelledby="titulo-do-cofre">
      <h2 className="titulo-secao" id="titulo-do-cofre">
        Credenciais no cofre
      </h2>
      <CorpoDoCofre implantacaoId={implantacaoId} />
    </section>
  );
}

function CorpoDoCofre({ implantacaoId }: { implantacaoId: string }) {
  const sessao = useSessao();
  const podeVer = podeNaSessao(sessao.data, PERMISSAO_DE_VER_COFRE);
  const cofre = useCofreDaImplantacao(podeVer ? implantacaoId : "");

  if (!podeVer) {
    return (
      <Aviso tom="marca" data-testid="sem-infra-ver">
        <b>As credenciais do cofre são vistas pela equipe de infraestrutura.</b> Se você precisa
        de um destes acessos, peça a ela — o pedido fica registrado.
      </Aviso>
    );
  }

  if (cofre.isPending) {
    return (
      <p className="lead" role="status">
        Carregando as credenciais…
      </p>
    );
  }

  if (cofre.isError) {
    return (
      <Aviso tom="erro" data-testid="falha-do-cofre-no-console">
        {cofre.error instanceof ErroDaApi
          ? cofre.error.message
          : "Não foi possível carregar o cofre agora."}
      </Aviso>
    );
  }

  if (cofre.data.segredos.length === 0) {
    return (
      <Aviso tom="marca" data-testid="cofre-vazio-no-console">
        <b>Este cliente ainda não entregou nenhuma credencial pelo portal.</b> Elas chegam pelo
        questionário de infraestrutura.
      </Aviso>
    );
  }

  return (
    <>
      {/* Rótulo, procedência e histórico — nunca valor. `esquemaSegredo` não declara campo de
          valor e o zod descarta chave desconhecida: a ausência é a tranca, não a disciplina de
          quem escreve a tela. Revelar e descartar têm tela própria, com motivo escrito e relógio;
          um botão de revelar no meio de um resumo transformaria um ato registrado em clique de
          passagem. */}
      <div data-testid="segredos-da-implantacao">
        <Tabela legenda="Credenciais guardadas no cofre — a lista, nunca os valores.">
        <Cabecalho>
          <Linha>
            <CelulaCabecalho>Credencial</CelulaCabecalho>
            <CelulaCabecalho>Estado</CelulaCabecalho>
            <CelulaCabecalho>Entregue por</CelulaCabecalho>
            <CelulaCabecalho>Proteção</CelulaCabecalho>
            <CelulaCabecalho>Revelações</CelulaCabecalho>
          </Linha>
        </Cabecalho>
        <Corpo>
          {cofre.data.segredos.map((segredo) => (
            <Linha key={segredo.id}>
              <Celula className="celula-nome">
                {segredo.rotulo}
                <div className="celula-secundaria">
                  {segredo.origem_pergunta_id
                    ? "entregue no questionário de infraestrutura"
                    : "registrada pela equipe"}
                </div>
              </Celula>
              <Celula>
                <Chip
                  estado={
                    segredo.descartado
                      ? "neutro"
                      : segredo.expirado
                        ? "erro"
                        : segredo.sob_custodia_provisoria
                          ? "atencao"
                          : "ok"
                  }
                >
                  {segredo.descartado
                    ? "descartada"
                    : segredo.expirado
                      ? "vencida"
                      : segredo.sob_custodia_provisoria
                        ? "chave provisória"
                        : "guardada"}
                </Chip>
              </Celula>
              <Celula className="celula-secundaria">
                {segredo.criado_por?.nome ?? "—"}
                <div>{formatarInstante(segredo.criado_em) ?? "—"}</div>
              </Celula>
              <Celula className="celula-secundaria">
                {ROTULO_DA_CUSTODIA_DO_SEGREDO[segredo.custodia]}
              </Celula>
              <Celula className="numero-tabular">
                {segredo.revelacoes}
                <div className="celula-secundaria">
                  {segredo.revelacoes === 0
                    ? "nunca vista"
                    : `última em ${formatarInstante(segredo.ultima_revelacao_em) ?? "—"}`}
                </div>
              </Celula>
            </Linha>
          ))}
          </Corpo>
        </Tabela>
      </div>

      <div className="acoes-do-console mt-4">
        <Link to={caminhoDoCofre(implantacaoId)} className="botao" data-testid="ver-cofre">
          Abrir o cofre
        </Link>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ consultor da conta */

/**
 * O consultor da conta — quem fechou a venda e quem o cliente procura.
 *
 * Consultor e responsável pela venda eram dois campos; desde a 0105 (requisito 3 da Amanda,
 * 01/10/2026) são um só. Ele nasce no cadastro e pode ser trocado aqui; não pode ficar vazio.
 *
 * **A frase de baixo não é decoração.** Trocar o consultor troca o contato que o CLIENTE vê na
 * tela dele (até o módulo dele chegar em Treinamento, quando passa a valer o responsável da
 * tarefa). Quem clica precisa saber que o efeito atravessa a fronteira e chega do outro lado.
 */
function ConsultorDaConta({ esteira }: { esteira: Esteira }) {
  const sessao = useSessao();
  const podeEditar = podeNaSessao(sessao.data, PERMISSAO_DE_EDITAR_IMPLANTACAO);
  /* A lista de consultores só serve ao seletor. Pedi-la sem poder editar custava um 403 no
     console da infraestrutura, que abre esta tela para liberar o ambiente e não tem o catálogo. */
  const catalogo = useCatalogo(podeEditar);
  const definir = useDefinirConsultor(esteira.implantacao_id);

  if (!podeEditar) {
    return <Dado rotulo="Consultor da conta">{esteira.consultor?.nome ?? "—"}</Dado>;
  }

  return (
    <Dado rotulo="Consultor da conta">
      <select
        className="campo__controle"
        value={esteira.consultor?.id ?? ""}
        disabled={definir.isPending}
        data-testid="consultor-da-implantacao"
        aria-label="Consultor da conta"
        onChange={(evento) => {
          if (evento.target.value) definir.mutate(evento.target.value);
        }}
      >
        {esteira.consultor ? null : <option value="">—</option>}
        {(catalogo.data?.consultores ?? []).map((pessoa) => (
          <option key={pessoa.id} value={pessoa.id}>
            {pessoa.nome}
          </option>
        ))}
      </select>
      <span className="dado-secundario" data-testid="efeito-do-consultor">
        É quem fechou a venda e quem o cliente vê como contato na tela dele, até o módulo dele
        chegar em Treinamento.
      </span>
      {definir.isError ? (
        <span className="dado-secundario" data-testid="falha-do-consultor">
          {mensagemDoConsole(definir.error)}
        </span>
      ) : null}
    </Dado>
  );
}

/* ---------------------------------------------------------------------------- contrato */

/**
 * O PDF do contrato assinado, **só leitura**: o link de download e de quando é. Fica dentro do
 * cartão "Contrato", que leva o selo "somente leitura" — por isso nada aqui pode aceitar mão. A
 * ação de anexar ou substituir mora no cartão à parte (`AnexarContrato`), que tem selo próprio.
 */
function ContratoAssinado({ esteira }: { esteira: Esteira }) {
  const contrato = esteira.contrato;
  return (
    <Dado rotulo="Contrato assinado">
      {contrato ? (
        <>
          <a href={caminhoDoContrato(esteira.implantacao_id)} download data-testid="baixar-contrato">
            Baixar contrato assinado
          </a>
          <span className="dado-secundario">
            {contrato.nome}
            {contrato.substituido_em
              ? ` · substituído em ${formatarData(contrato.substituido_em)}`
              : ` · anexado em ${formatarData(contrato.anexado_em)}`}
          </span>
        </>
      ) : (
        <span data-testid="contrato-nao-anexado">Contrato não anexado</span>
      )}
    </Dado>
  );
}

/**
 * Anexar ou substituir o PDF. É a única ação do bloco do contrato, e por isso tem cartão próprio,
 * tracejado como o do consultor da conta: o selo "somente leitura" do cartão "Contrato" ao lado não
 * pode conviver com um botão dentro dele. Quem não edita a implantação não vê o cartão.
 * Implantação aberta antes da regra não tem arquivo: aqui é o caminho de regularização.
 */
function AnexarContrato({ esteira }: { esteira: Esteira }) {
  const sessao = useSessao();
  const podeEditar = podeNaSessao(sessao.data, PERMISSAO_DE_EDITAR_IMPLANTACAO);
  const anexar = useAnexarContrato(esteira.implantacao_id);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [erro, setErro] = useState<string | undefined>();
  const contrato = esteira.contrato;

  if (!podeEditar) return null;

  function enviar() {
    const problema = erroDoContrato(arquivo);
    setErro(problema);
    if (problema || !arquivo) return;
    anexar.mutate(arquivo, { onSuccess: () => setArquivo(null) });
  }

  return (
    <Cartao
      titulo={contrato ? "Substituir o contrato assinado" : "Anexar o contrato assinado"}
      className="cartao--editavel mt-3"
      data-testid="anexar-o-contrato"
      acessorio={<span className="selo-de-modo selo-de-modo--editavel">editável</span>}
    >
      <div className="flex flex-wrap items-center gap-2 max-w-md">
        <input
          type="file"
          accept="application/pdf,.pdf"
          aria-label={contrato ? "Novo contrato assinado (PDF)" : "Contrato assinado (PDF)"}
          data-testid="arquivo-do-contrato"
          onChange={(evento) => setArquivo(evento.target.files?.[0] ?? null)}
        />
        <Botao
          tamanho="pequeno"
          variante="fantasma"
          type="button"
          disabled={anexar.isPending}
          onClick={enviar}
          data-testid="anexar-contrato"
        >
          {contrato ? "Substituir contrato" : "Anexar contrato"}
        </Botao>
      </div>
      {erro ? (
        <p role="alert" className="mt-2" data-testid="erro-do-contrato">
          {erro}
        </p>
      ) : null}
      {anexar.isError ? (
        <p role="alert" className="mt-2" data-testid="falha-do-contrato">
          {mensagemDoConsole(anexar.error)}
        </p>
      ) : null}
    </Cartao>
  );
}

/**
 * Os dados do cliente em cartões por assunto (pedido da Amanda, item 15): identificação,
 * contrato, ambiente e escopo — todos de LEITURA — e, à parte, o consultor da conta, que é o único
 * ponto editável do bloco. Misturar o seletor de consultor com a data de assinatura fazia o consultor
 * procurar o que pode mexer; agora o cartão tracejado é o único que aceita mão.
 */
function Contrato({
  esteira,
  podeVerContrato,
  detalhe,
}: {
  esteira: Esteira;
  podeVerContrato: boolean;
  detalhe: ReturnType<typeof useDetalheDaImplantacao>["data"];
}) {
  const organizacao = detalhe?.organizacao ?? esteira.organizacao;
  return (
    <>
      {!podeVerContrato || detalhe === null || detalhe === undefined ? (
        <Aviso tom="marca" className="mb-3" data-testid="sem-contrato-ver">
          <b>Ambiente, ERP e situação comercial ficam com o comercial e a consultoria.</b> O prazo
          abaixo continua visível para você.
        </Aviso>
      ) : null}

      <div className="grid gap-3 md:grid-cols-2" data-testid="cartoes-do-cliente">
        <Cartao
          titulo="Identificação"
          className="cartao--leitura"
          data-testid="dados-de-identificacao"
          acessorio={<span className="selo-de-modo">somente leitura</span>}
        >
          <dl className="grid gap-3 sm:grid-cols-2 m-0">
            <Dado rotulo="Razão social">{organizacao.razao_social}</Dado>
            <Dado rotulo="Nome fantasia">{organizacao.nome_fantasia ?? "—"}</Dado>
            <Dado rotulo="CNPJ">
              <span className="numero-tabular">{formatarCnpj(organizacao.cnpj)}</span>
            </Dado>
            <Dado rotulo="Cidade">
              {organizacao.cidade ? `${organizacao.cidade}/${organizacao.uf}` : "—"}
            </Dado>
          </dl>
        </Cartao>

        <Cartao
          titulo="Contrato"
          className="cartao--leitura"
          data-testid="dados-do-contrato"
          acessorio={<span className="selo-de-modo">somente leitura</span>}
        >
          <dl className="grid gap-3 sm:grid-cols-2 m-0">
            <Dado rotulo="Assinatura">
              <span className="numero-tabular">
                {formatarData(esteira.prazo.contrato_assinado_em) ?? "—"}
              </span>
            </Dado>
            <Dado rotulo="Prazo contratado">
              <span className="numero-tabular">{esteira.prazo.prazo_dias}</span> dias
            </Dado>
            <ContratoAssinado esteira={esteira} />
            {detalhe ? (
              <>
                <Dado rotulo="Situação">{rotuloDaSituacao(detalhe.situacao_cliente)}</Dado>
                <Dado rotulo="Estado">{rotuloDoEstado(detalhe.estado)}</Dado>
              </>
            ) : null}
          </dl>
        </Cartao>

        {detalhe ? (
          <Cartao
            titulo="Ambiente"
            className="cartao--leitura"
            data-testid="dados-do-ambiente"
            acessorio={<span className="selo-de-modo">somente leitura</span>}
          >
            <dl className="grid gap-3 sm:grid-cols-2 m-0">
              <Dado rotulo="Ambiente">{rotuloDoAmbiente(detalhe.ambiente_tipo)}</Dado>
              <Dado rotulo="ERP do cliente">{detalhe.erp ?? "não informado"}</Dado>
              <Dado rotulo="Épico no Jira">
                {detalhe.jira_epic_key ? (
                  <code>{detalhe.jira_epic_key}</code>
                ) : (
                  <>
                    ainda não criado
                    <span className="dado-secundario">
                      a tarefa de Kick Off é criada dentro dele
                    </span>
                  </>
                )}
              </Dado>
            </dl>
          </Cartao>
        ) : null}

        <Cartao
          titulo="Escopo: o que o cliente contratou"
          className="cartao--leitura"
          data-testid="escopo-da-implantacao"
          acessorio={<span className="selo-de-modo">somente leitura</span>}
        >
          <ul className="lista-de-marcas" data-testid="modulos-da-implantacao">
            {esteira.modulos.map((modulo) => (
              <li className="lista-de-marcas__item" key={modulo.chave}>
                {modulo.nome}
                {modulo.jira_task_key ? (
                  <span className="dado-secundario">
                    <code>{modulo.jira_task_key}</code>
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
          {esteira.recursos_plus.length > 0 ? (
            <>
              <p className="campo__rotulo mt-4">Recursos Plus</p>
              <ul className="lista-de-marcas">
                {esteira.recursos_plus.map((recurso) => (
                  <li className="lista-de-marcas__item" key={recurso.chave}>
                    {recurso.nome}
                  </li>
                ))}
              </ul>
            </>
          ) : null}
          <p className="nota-rodape mt-3">
            O kickoff só pergunta sobre os módulos contratados. Módulo contratado que ainda não tem
            perguntas aparece como lacuna.
          </p>
        </Cartao>
      </div>

      <AnexarContrato esteira={esteira} />

      <AtribuicaoDaImplantacao esteira={esteira} />
    </>
  );
}

/** O único cartão editável do bloco: o consultor da conta, quem conduz a implantação. */
function AtribuicaoDaImplantacao({ esteira }: { esteira: Esteira }) {
  const sessao = useSessao();
  const podeEditar = podeNaSessao(sessao.data, PERMISSAO_DE_EDITAR_IMPLANTACAO);
  return (
    <Cartao
      titulo="Consultor da conta"
      className="cartao--editavel mt-3"
      data-testid="atribuicao-da-implantacao"
      acessorio={
        <span className="selo-de-modo selo-de-modo--editavel">
          {podeEditar ? "editável" : "somente leitura"}
        </span>
      }
    >
      <dl className="m-0 max-w-md">
        <ConsultorDaConta esteira={esteira} />
      </dl>
    </Cartao>
  );
}

/* --------------------------------------------------------------------------- histórico */

function Historico({ implantacaoId }: { implantacaoId: string }) {
  const eventos = useLinhaDoTempo(implantacaoId);

  return (
    /* Sem título próprio: o h2 da seção já diz "Linha do tempo", e dois títulos iguais seguidos
       confundem quem navega por títulos com leitor de tela. */
    <Cartao>
      <p className="cartao__texto mb-3">
        O histórico da implantação. Nada aqui é editado ou apagado — é o registro que resolve dúvida
        de prazo.
      </p>
      {eventos.isPending ? (
        <p className="cartao__texto" role="status">
          Carregando a linha do tempo…
        </p>
      ) : eventos.isError ? (
        <Aviso tom="erro" data-testid="falha-da-linha-do-tempo">
          {eventos.error instanceof ErroDaApi
            ? eventos.error.message
            : "Não foi possível carregar a linha do tempo agora."}
        </Aviso>
      ) : (
        <LinhaDoTempo eventos={eventos.data.eventos} comFiltro />
      )}
    </Cartao>
  );
}
